import React, {useState} from 'react';
import {InstanceProps} from 'react-modal-promise';

import {ModalReact} from 'common/components/modal';
import {InputReact} from 'common/components/input';
import {InputLabelReact} from 'common/components/input/label';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ValidationStatus} from 'common/components/input/types';
import {useTranslation} from 'common/i18n/hooks';
import {SectionItem} from 'common/components/items-menu/types';

import type {AggregationSelector, ModalResolvedValue} from './constants';
import styles from './custom-percentile-modal.module.styl';

export const CustomPercentileModal = (props: InstanceProps<ModalResolvedValue> & ModalResolvedValue) => {
  const {t} = useTranslation();
  const [percentileValue, setPercentileValue] = useState(``);
  const [error, setError] = useState(``);

  const validatePercentileValue = (value: string): string => {
    if (!value.trim()) {
      return ``;
    }

    const numericValue = parseFloat(value);

    if (isNaN(numericValue)) {
      return t(`query-builder.count-menu.custom-percentile-modal.error-invalid-number`);
    }

    if (numericValue <= 0 || numericValue >= 100) {
      return t(`query-builder.count-menu.custom-percentile-modal.error-out-of-range`);
    }

    return ``;
  };

  const handleValueChange = (value: string) => {
    setPercentileValue(value);
    setError(validatePercentileValue(value));
  };

  const handleConfirm = () => {
    if (error || !percentileValue.trim()) {
      return;
    }

    const modifiedAggregation: SectionItem<AggregationSelector> = {
      ...props.aggregation,
      value: {
        ...props.aggregation.value,
        percentile: parseFloat(percentileValue),
      } as AggregationSelector,
    };

    props.onResolve({
      aggregation: modifiedAggregation,
      subSelectionIndex: props.subSelectionIndex,
    });

    // Reset state
    setPercentileValue(``);
    setError(``);
  };

  const handleCancel = () => {
    setPercentileValue(``);
    setError(``);
    props.onResolve(null);
  };

  const handleClose = (ev: any) => {
    if (ev.detail.state === `closed`) {
      handleCancel();
    }
  };

  return (
    <ModalReact
      open
      modalTitle={t(`query-builder.count-menu.custom-percentile-modal.title`)}
      modalDescription='Modal for entering custom percentile value'
      growToContent
      onChange={handleClose}
    >
      <div className={styles.content}>
        <InputLabelReact>{t(`query-builder.count-menu.custom-percentile-modal.label`)}</InputLabelReact>
        <InputReact
          type='number'
          value={percentileValue}
          onInput={handleValueChange}
          placeholder={t(`query-builder.count-menu.custom-percentile-modal.placeholder`)}
          validationStatus={error ? ValidationStatus.Invalid : ValidationStatus.Default}
          errorMessage={error}
        />
        <div className={styles.actions}>
          <ButtonReact theme={ButtonTheme.Secondary} onClick={handleCancel}>
            {t(`common.cancel`)}
          </ButtonReact>
          <ButtonReact
            theme={ButtonTheme.Primary}
            onClick={handleConfirm}
            disabled={!percentileValue.trim() || !!error}
          >
            {t(`common.confirm`)}
          </ButtonReact>
        </div>
      </div>
    </ModalReact>
  );
};
